import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useMaintenanceMode } from '../../admin-panel/context/MaintenanceContext';
import './MaintenanceNoticeBanner.css';

const STORAGE_KEY = 'maintenanceNoticeDismissed';

/**
 * Format ISO date in user's local time and get timezone abbreviation (e.g. "CST").
 * Returns { formatted, timeZoneName } so we can show "(your local time)".
 */
function formatScheduledTimeWithZone(isoString) {
  if (!isoString) return { formatted: '', timeZoneName: '' };
  try {
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return { formatted: '', timeZoneName: '' };
    const formatted = d.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
    let timeZoneName = '';
    try {
      const parts = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' }).formatToParts(d);
      const tz = parts.find((p) => p.type === 'timeZoneName');
      if (tz) timeZoneName = tz.value;
    } catch {
      // ignore
    }
    return { formatted, timeZoneName };
  } catch {
    return { formatted: '', timeZoneName: '' };
  }
}

function getDismissedKey(scheduledStartTime) {
  return scheduledStartTime ? `${STORAGE_KEY}:${scheduledStartTime}` : null;
}

/**
 * Small banner shown on the main app when maintenance is scheduled for a future time.
 * Shown for everyone (including admins) on /app routes. Users can dismiss it; dismissal
 * is stored per scheduled time so a new schedule will show the banner again.
 */
const MaintenanceNoticeBanner = () => {
  const location = useLocation();
  const { isMaintenanceMode, scheduledStartTime, loading } = useMaintenanceMode();
  const [dismissed, setDismissed] = useState(false);

  const dismissedKey = getDismissedKey(scheduledStartTime);

  useEffect(() => {
    if (!dismissedKey) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(localStorage.getItem(dismissedKey) === '1');
    } catch {
      setDismissed(false);
    }
  }, [dismissedKey]);

  const handleDismiss = useCallback(() => {
    if (!dismissedKey) return;
    try {
      localStorage.setItem(dismissedKey, '1');
      setDismissed(true);
    } catch {
      setDismissed(true);
    }
  }, [dismissedKey]);

  const isAppRoute = location.pathname.startsWith('/app') && !location.pathname.startsWith('/portal');
  const isScheduledFuture = scheduledStartTime && new Date(scheduledStartTime) > new Date();
  const show = !loading && !isMaintenanceMode && isScheduledFuture && isAppRoute && !dismissed;

  if (!show) return null;

  const { formatted, timeZoneName } = formatScheduledTimeWithZone(scheduledStartTime);
  if (!formatted) return null;

  const timeLabel = timeZoneName
    ? `${formatted} ${timeZoneName} (your local time)`
    : `${formatted} (your local time)`;

  return (
    <div
      className="maintenance-notice-banner"
      role="status"
      style={{
        background: 'linear-gradient(90deg, #1565c0 0%, #1976d2 100%)',
        color: '#ffffff'
      }}
    >
      <span className="maintenance-notice-banner__icon" aria-hidden="true" style={{ color: '#ffffff' }}>⚠</span>
      <span className="maintenance-notice-banner__text" style={{ color: '#ffffff' }}>
        Scheduled maintenance at {timeLabel}. The app will be unavailable during this time.
      </span>
      <button
        type="button"
        className="maintenance-notice-banner__close"
        onClick={handleDismiss}
        aria-label="Dismiss maintenance notice"
        style={{ color: '#ffffff' }}
      >
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
};

export default MaintenanceNoticeBanner;
